import React from 'react';

interface TaxIntelProps {
    taxType: string | undefined;
    cgstRate: number;
    sgstRate: number;
    igstRate: number;
    onChange: (field: any, value: any) => void;
}

/**
 * TaxIntel Component
 * Standardized UI for selecting Tax Type (AUTO, INTRA, INTER) 
 * and managing manual GST overrides.
 */
const TaxIntel: React.FC<TaxIntelProps> = ({
    taxType = 'auto',
    cgstRate,
    sgstRate,
    igstRate,
    onChange
}) => {
    return (
        <div className="space-y-3">
            {/* Tax Type Selector */}
            <div className="flex bg-slate-100/80 p-1 rounded-xl border border-slate-200/50 shadow-inner">
                <button 
                    type="button" 
                    onClick={() => onChange('tax_type', 'auto')}
                    className={`flex-1 py-1.5 text-[10px] font-black rounded-lg transition-all duration-200 uppercase tracking-widest ${taxType === 'auto' ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200/50' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    Auto
                </button>
                <button 
                    type="button" 
                    onClick={() => onChange('tax_type', 'cgst_sgst')}
                    className={`flex-1 py-1.5 text-[10px] font-black rounded-lg transition-all duration-200 uppercase tracking-widest ${taxType === 'cgst_sgst' ? 'bg-white text-emerald-600 shadow-sm ring-1 ring-slate-200/50' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    Intra
                </button>
                <button 
                    type="button" 
                    onClick={() => onChange('tax_type', 'igst')}
                    className={`flex-1 py-1.5 text-[10px] font-black rounded-lg transition-all duration-200 uppercase tracking-widest ${taxType === 'igst' ? 'bg-white text-amber-600 shadow-sm ring-1 ring-slate-200/50' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    Inter
                </button>
            </div>

            {/* Manual Override Fields */}
            <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm transition-all">
                {taxType === 'auto' ? (
                    <div className="text-[10px] font-bold text-slate-400 text-center py-2 uppercase tracking-widest">
                        Taxes auto-calculated based on firm & client state.
                        <br/><span className="text-indigo-400 font-black">Select Intra/Inter to override.</span>
                    </div>
                ) : (
                    <div className="flex items-center gap-4 animate-in zoom-in-95 duration-300">
                        <div className="flex-1 grid grid-cols-2 gap-4">
                            {taxType === 'cgst_sgst' ? (
                                <>
                                    <div className="relative">
                                        <label className="absolute -top-2 left-2 px-1 bg-white text-[9px] text-emerald-600 font-black uppercase tracking-widest z-10">CGST %</label>
                                        <input 
                                            type="number" 
                                            value={cgstRate} 
                                            onChange={(e) => onChange('cgst_rate', parseFloat(e.target.value) || 0)}
                                            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-black text-center text-slate-900 outline-none focus:border-emerald-500 focus:bg-white transition-all" 
                                        />
                                    </div>
                                    <div className="relative">
                                        <label className="absolute -top-2 left-2 px-1 bg-white text-[9px] text-emerald-600 font-black uppercase tracking-widest z-10">SGST %</label>
                                        <input 
                                            type="number" 
                                            value={sgstRate} 
                                            onChange={(e) => onChange('sgst_rate', parseFloat(e.target.value) || 0)}
                                            className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-black text-center text-slate-900 outline-none focus:border-emerald-500 focus:bg-white transition-all" 
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="relative col-span-2">
                                    <label className="absolute -top-2 left-3 px-1 bg-white text-[9px] text-amber-600 font-black uppercase tracking-widest z-10">IGST RATE %</label>
                                    <input 
                                        type="number" 
                                        value={igstRate} 
                                        onChange={(e) => onChange('igst_rate', parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-black text-center text-slate-900 outline-none focus:border-amber-500 focus:bg-white transition-all" 
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaxIntel;
